import React from 'react';

import type { Page, WebsitePicture } from '@/payload/payload-types';
import RichTextLexical from '../../_components/RichTextLexical';
import Image from 'next/image';
import { cn } from '@shadcn/lib/utils';
import { CtaButton } from '../CtaButton';
import { InfoBoxes } from '../InfoBoxes';

type HeroProptypes = Extract<Page['layoutBlocks'][0], { blockType: 'hero' }>;

type HeroImageProptypes = {
	alt: WebsitePicture['alt'];
	url: WebsitePicture['url'];
	className?: string;
};

const HeroImage: React.FC<HeroImageProptypes> = ({ alt, url, className }) => {
	return (
		<Image
			className={cn('rounded-3xl', className)}
			width={600}
			height={440}
			alt={alt}
			src={url || '/assets/broken-image.jpg'}
			priority
		/>
	);
};

export const Hero: React.FC<HeroProptypes> = props => {
	const {
		heading,
		description,
		ctaButton,
		enableImage,
		image,
		enableInfoBoxes,
		infoboxes,
		enableDescription,
	} = props;

	if (typeof image === 'string') {
		throw new Error('Something went wrong. Bad image depth for hero image.');
	}

	const heroImage = image?.sizes?.hero;
	const hasImage = enableImage && image;

	return (
		<section className={`flex items-stretch space-x-20 ${hasImage ? 'min-h-[440px]' : ''}`}>
			<div
				className={`flex w-full flex-col space-y-10 ${hasImage ? 'lg:w-6/12' : 'lg:w-7/12'}`}
			>
				<div className="flex flex-col space-y-4">
					<h1 className="text-5xl leading-[48px] font-light lg:text-[72px] lg:leading-[72px]">
						<RichTextLexical content={heading} />
					</h1>
				</div>
				{hasImage && (
					<HeroImage className="block lg:hidden" alt={image.alt} url={heroImage?.url} />
				)}
				{enableDescription && (
					<RichTextLexical
						className="text-base leading-8 font-normal lg:text-xl lg:leading-10"
						content={description}
					/>
				)}

				{enableInfoBoxes && infoboxes && <InfoBoxes {...infoboxes} />}

				{ctaButton?.label && <CtaButton ctaButton={ctaButton} />}
			</div>
			{hasImage && (
				<div className="3xl:block absolute right-0 lg:w-5/12 lg:overflow-visible">
					<HeroImage
						className="3xl:w-fit 3xl:rounded-3xl inset-0 hidden h-[440px] w-full rounded-r-none object-cover lg:block"
						alt={image.alt}
						url={heroImage?.url}
					/>
				</div>
			)}
		</section>
	);
};
